// src/pages/income/_shared/IncomeDraftNew.jsx
// ✅ 수입데이터+수입기안 통합 작성 [파일럿: 경영팀] — 지출 쪽 _shared/ExpenseDraftNew.jsx와
// 같은 목적(데이터+기안 동시 생성, 결재라인+붙임, 미리보기 새 창 실시간 동기화)이지만
// 수입 쪽은 품목/인건비/다중거래처 패널이 없어(거래처 1곳 + 금액 단건이 기본형) 훨씬
// 얇게 새로 설계했다. 사업별 차이(API 경로/수입 종류 목록/거래처 API)는 config로 받는다.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";
import { draftNumberChangedLine, formatDraftSaveError } from "../../../utils/draftNumberNotice";
import ApprovalLine from "../../../components/approval-line/ApprovalLine";
import ApprovalLineModal from "../../../components/approval-line/ApprovalLineModal";
import ApprovalLineConfirmDialog from "../../../components/approval-line/ApprovalLineConfirmDialog";

const OTHER_CODE = "__other__";
const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];
const PAY_METHOD_TO_CODE = { 계좌이체: "transfer", 카드결제: "card", 현금결제: "cash" };

function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)}${units[i]}`;
}

// ✅ IncomeProposalPreview.jsx가 받는 formData 형태로 조립 — 그 컴포넌트를 그대로
// 재사용하기 위한 순수 함수(draft_document/DocumentPreview.jsx의 income 분기와 동일한
// 필드 이름을 쓴다). previewFormData useMemo와 pickCategory(종류 선택 직후 팝업을
// 동기적으로 열 때 방금 계산한 "새 값"을 직접 넘겨야 함) 양쪽에서 공유해서 쓴다.
export function buildPreviewFormData(category, ctx) {
  return {
    recipient: "내부결재",
    title: ctx.title,
    documentType: "income_draft",
    businessType: category,
    detailType: ctx.subcategory,
    detailOption: ctx.subcategory,
    workMonth: ctx.workMonth,
    startDate: ctx.workMonth,
    endDate: ctx.workMonth,
    client: ctx.partnerName,
    amount: ctx.amount,
    transactionType: {
      계좌이체: ctx.payMethodLabel === "계좌이체",
      카드결제: ctx.payMethodLabel === "카드결제",
      현금결제: ctx.payMethodLabel === "현금결제",
    },
    manualDate: ctx.claimDate,
    documentNumber: ctx.draftNo,
    content: ctx.desc,
    attachments: ctx.attachments.filter((a) => a.name.trim() !== ""),
    // ✅ 환경/종합 "냉난방기 종합세척" 전용 — IncomeProposalPreview.jsx가
    // businessType==="환경/종합" && detailType==="냉난방기 종합세척"일 때만 사용.
    aircondDetails: ctx.aircondDetails || [],
  };
}

const BTN_TEXT_COLOR = "#222";
const PICK_BTN_STYLE = {
  padding: "16px 8px",
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 8,
  cursor: "pointer",
  color: BTN_TEXT_COLOR,
};
const HEADER_BTN_STYLE = {
  width: "auto",
  flexShrink: 0,
  padding: "8px 20px",
  fontSize: 14,
  whiteSpace: "nowrap",
  color: BTN_TEXT_COLOR,
  background: "#fff",
  border: "1px solid #ccc",
  borderRadius: 6,
  cursor: "pointer",
};
const SAVE_HEADER_BTN_STYLE = {
  ...HEADER_BTN_STYLE,
  color: "#fff",
  background: "#0b5fff",
  border: "none",
  fontWeight: 600,
};
const ROW_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "130px 1fr",
  gap: "8px 12px",
  alignItems: "stretch",
  marginBottom: 5,
  minHeight: 36,
};
const ROW_LABEL_STYLE = {
  fontWeight: 600,
  color: "#374151",
  fontSize: 14,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#f9fafb",
  boxSizing: "border-box",
  height: 36,
  padding: "0 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  whiteSpace: "nowrap",
};
const ROW_INPUT_STYLE = {
  width: "100%",
  height: 36,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "0 10px",
  fontSize: 14,
  lineHeight: "1.4",
  boxSizing: "border-box",
};
const ROW_INPUT_READONLY_STYLE = { ...ROW_INPUT_STYLE, backgroundColor: "#e5e7eb" };
const ROW_TEXTAREA_STYLE = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "4px 8px",
  fontSize: 14,
  lineHeight: "1.3",
  boxSizing: "border-box",
};
const ATTACHMENT_STATUS_COL_WIDTH = 92;
const ATTACHMENT_ACTION_COL_WIDTH = 200;
const ATTACHMENT_COUNT_COL_WIDTH = 50;
const ATTACHMENT_UNIT_COL_WIDTH = 18;
const ATTACHMENT_REMOVE_COL_WIDTH = 28;
const ATTACHMENT_ROW_HEIGHT = 36;
const ATTACHMENT_ROW_ITEM_STYLE = { height: ATTACHMENT_ROW_HEIGHT, boxSizing: "border-box" };

export default function IncomeDraftNew({ config }) {
  const {
    category, businessLabel, tabTitle, draftNoPrefix, combinedCreateApi,
    listRoutePath, previewRoutePath, categories, partnerApiBase, otherCategoryLabel,
    previewStorageKey, previewChannelName, listRefreshChannelName, previewWindowName,
    DetailPanel, detailPanelCategories, detailPanelPayloadKey, defaultAttachmentsByCategory,
    useClaimTitleTemplate,
  } = config;

  const navigate = useNavigate();

  useEffect(() => { document.title = `수입기안 - ${tabTitle}`; }, [tabTitle]);

  const [step, setStep] = useState("pick"); // "pick" | "form"
  const [subcategory, setSubcategory] = useState("");

  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [claimDate, setClaimDate] = useState(() => toISO(new Date()));
  const [workMonth, setWorkMonth] = useState(() => toISO(new Date()).slice(0, 7));
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");
  const [desc, setDesc] = useState("");

  // 거래처 — 수입은 1곳(단건)만 입력. partnerApiBase가 가리키는 Partner 모델 자동완성.
  const [partners, setPartners] = useState([]);
  const [partnerInput, setPartnerInput] = useState("");
  const [partnerId, setPartnerId] = useState(null);
  const [showPartnerList, setShowPartnerList] = useState(false);
  const [partnerHighlight, setPartnerHighlight] = useState(-1);

  const [attachments, setAttachments] = useState([]);
  // ✅ 종류별 전용 상세내역(예: 환경/종합 냉난방기 종합세척) — config.DetailPanel이
  // 있을 때만 쓰인다. 대부분 사업은 항상 빈 배열.
  const [detailRows, setDetailRows] = useState([]);
  const showDetailPanel = DetailPanel && detailPanelCategories?.includes(subcategory);

  const [approvers, setApprovers] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);

  const previewWindowRef = useRef(null);
  const previewChannelRef = useRef(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);
  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;
    previewChannelRef.current = new BroadcastChannel(previewChannelName);
    return () => previewChannelRef.current?.close();
  }, [previewChannelName]);

  useEffect(() => {
    return () => {
      try {
        new BroadcastChannel(previewChannelName).postMessage({ type: "clear" });
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [saving, setSaving] = useState(false);
  const [seq, setSeq] = useState("001");

  const [savedDraftId, setSavedDraftId] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [failedUploads, setFailedUploads] = useState([]);
  const fileInputRef = useRef(null);

  // ✅ 거래처 목록 로드 — 최초 마운트 + focus 복귀 시(거래처 관리 새 창에서 등록 후
  // 돌아왔을 때) 재조회. 지출 쪽 VendorPanel과 동일한 이유(등록은 공용 관리 페이지에서
  // 일어나므로 이 페이지 쪽 focus 리스너가 재조회하는 게 가장 간단하다).
  async function loadPartners() {
    try {
      const { data } = await api.get(partnerApiBase);
      setPartners(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error("❌ 거래처 목록 불러오기 실패:", err);
    }
  }
  useEffect(() => { loadPartners(); }, [partnerApiBase]);
  useEffect(() => {
    const handleFocus = () => loadPartners();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerApiBase]);

  const filteredPartners = partners.filter((p) =>
    p.name.toLowerCase().includes(partnerInput.toLowerCase())
  );

  // ✅ partner_name 데이터 정합성 방어 — partnerId가 선택돼 있으면 그 순간의
  // partners 목록에서 실제 이름을 다시 찾아 쓴다. partnerInput(자유 입력 텍스트
  // state)만 믿고 보내면 partnerId는 정상인데 partner_name만 빈 채로 저장되는
  // 사고가 있었다(2026-07-30 f9ca240 커밋에서 처음 발견 — 수입목록 표시는 그때
  // partner?.name 우선 읽기로 고쳤지만, 기안 문서 제목/거래처는 여전히 이 desync에
  // 노출돼 있었음). partnerId 우선으로 재조회해 항상 실제 거래처명을 보낸다.
  const resolvedPartnerName = useMemo(() => {
    if (partnerId != null) {
      const matched = partners.find((p) => p.id === partnerId);
      if (matched) return matched.name;
    }
    return partnerInput;
  }, [partnerId, partners, partnerInput]);

  // ✅ 기안 문서 채번(next-seq) — DraftDocument.seq/draft_no용(지출 쪽과 동일 관례).
  // 수입 데이터 자체의 seq/draft_no는 서버가 별도로 authoritative하게 채번한다.
  // keep-alive 탭이라 재방문 시 마운트가 유지되므로 탭 활성화 때마다 다시 조회한다.
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/draft/documents/next-seq/", {
          params: { date: draftDate, category, document_type: "income_draft" },
        });
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (err) {
        console.error("❌ next-seq 오류:", err);
      }
    })();
    return () => { ignore = true; };
  }, [draftDate, category, seqRefreshTick]);

  const yymmdd = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);
  const draftNo = useMemo(() => `${draftNoPrefix}-${yymmdd}-${seq}호`, [draftNoPrefix, yymmdd, seq]);

  // ✅ 대금청구 제목 자동조립 — 구버전(draft_document/DraftDocumentNew.jsx)의
  // "{거래처} {세부구분} {N}월분 대금청구 건" 스펙 재현. config.useClaimTitleTemplate가
  // true인 사업(환경/소독)만 적용 — 거래처·세부구분·귀속월이 모두 채워지면 자동
  // 재계산한다(구버전 페이지도 이 제목 입력칸이 읽기전용이었을 만큼 완전 자동생성
  // 전용이라, 셋 중 하나가 바뀌면 그대로 덮어쓰는 게 원본 스펙과 동일한 동작).
  useEffect(() => {
    if (!useClaimTitleTemplate) return;
    if (subcategory === OTHER_CODE) return;
    if (!resolvedPartnerName || !subcategory || !workMonth) return;
    const [, month] = workMonth.split("-");
    const monthNum = parseInt(month, 10);
    if (!monthNum) return;
    const cleanVendor = resolvedPartnerName.replace(/\(.*?\)/g, "").trim();
    setTitle(`${cleanVendor} ${subcategory} ${monthNum}월분 대금청구 건`);
  }, [useClaimTitleTemplate, resolvedPartnerName, subcategory, workMonth]);

  // ── 종류 선택 ──────────────────────────────────────────────
  function pickCategory(value) {
    setSubcategory(value);

    const freshTitle = value !== OTHER_CODE ? `${businessLabel} ${value}` : "";
    setTitle(freshTitle);
    // ✅ 환경/소독 "소독방역"처럼 종류별 기본 붙임(예: "소독(방역) 청구서 1부")이
    // 있으면 채워서 시작 — 일반 붙임 행과 동일하게 이후 수정/삭제 가능.
    const defaultAtt = (value !== OTHER_CODE && defaultAttachmentsByCategory?.[value]) || [];
    const freshAttachments = defaultAtt.map((a, i) => ({ id: Date.now() + i, name: a.name, count: a.count, file: null }));
    setAttachments(freshAttachments);
    setAmount("");
    setDetailRows([]);
    setSavedDraftId(null);
    setFailedUploads([]);
    setStep("form");

    const freshValues = buildPreviewFormData(category, {
      title: freshTitle,
      subcategory: value !== OTHER_CODE ? value : otherCategoryLabel,
      workMonth,
      partnerName: "",
      amount: 0,
      payMethodLabel,
      claimDate,
      desc: "",
      attachments: freshAttachments,
      draftNo,
    });
    openPreviewWindow("preview", freshValues);
  }

  function backToPick() {
    setStep("pick");
  }

  // ── 붙임 ──────────────────────────────────────────────────
  const addAttachment = () => setAttachments((prev) => [...prev, { id: Date.now(), name: "", count: 1, file: null }]);
  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    setFailedUploads((prev) => prev.filter((a) => a.id !== id));
  };
  const setAttachmentField = (id, key, value) =>
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, [key]: value } : a)));

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setAttachments((prev) => [
      ...prev,
      ...files.map((file, i) => ({ id: Date.now() + i, name: file.name, count: 1, file })),
    ]);
    e.target.value = "";
  };

  async function uploadAttachmentFiles(draftId, rows) {
    const failures = [];
    for (const row of rows) {
      if (!row.file) continue;
      try {
        const formData = new FormData();
        formData.append("document", draftId);
        formData.append("file", row.file);
        await api.post("/draft/attachments/", formData);
      } catch (err) {
        console.error("❌ 첨부파일 업로드 실패:", row.file?.name, err.response?.data || err);
        failures.push(row);
      }
    }
    return failures;
  }

  async function retryFailedUploads() {
    if (!savedDraftId || failedUploads.length === 0) return;
    setUploadingFiles(true);
    const stillFailing = await uploadAttachmentFiles(savedDraftId, failedUploads);
    setUploadingFiles(false);
    setFailedUploads(stillFailing);
    if (stillFailing.length === 0) {
      previewChannelRef.current?.postMessage({ type: "clear" });
      try { new BroadcastChannel(listRefreshChannelName).postMessage({ type: "refresh" }); } catch {}
      alert("✅ 첨부파일까지 모두 업로드 완료!");
      navigate(listRoutePath);
    } else {
      alert(`⚠️ 여전히 ${stillFailing.length}개 파일 업로드에 실패했습니다.`);
    }
  }

  // ── 미리보기 ──────────────────────────────────────────────
  const previewFormData = useMemo(
    () => buildPreviewFormData(category, {
      title, subcategory: subcategory === OTHER_CODE ? otherCategoryLabel : subcategory,
      workMonth, partnerName: resolvedPartnerName, amount, payMethodLabel, claimDate, desc, attachments, draftNo,
      aircondDetails: detailRows,
    }),
    [category, title, subcategory, otherCategoryLabel, workMonth, resolvedPartnerName, amount, payMethodLabel, claimDate, desc, attachments, draftNo, detailRows]
  );

  function syncPreviewData(formData, approvalLine) {
    const payload = { formData, approvalLine };
    sessionStorage.setItem(previewStorageKey, JSON.stringify(payload));
    previewChannelRef.current?.postMessage({ type: "update", payload });
  }

  useEffect(() => {
    if (step !== "form") return;
    const timer = setTimeout(() => {
      syncPreviewData(previewFormData, approvers);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, previewFormData, approvers]);

  function openPreviewWindow(action = "preview", overrideFormData) {
    const formData = overrideFormData ?? previewFormData;
    syncPreviewData(formData, approvers);

    const path = `${previewRoutePath}${action !== "preview" ? `?action=${action}` : ""}`;
    if (previewWindowRef.current && !previewWindowRef.current.closed) {
      previewWindowRef.current.focus();
      if (action === "download" || action === "print") {
        previewWindowRef.current.location.search = `?action=${action}`;
      }
      setPreviewBlocked(false);
      return;
    }
    const win = window.open(path, previewWindowName, "width=900,height=1200");
    if (!win) {
      setPreviewBlocked(true);
      return;
    }
    previewWindowRef.current = win;
    setPreviewBlocked(false);
  }

  // ── 저장 ──────────────────────────────────────────────────
  async function handleSubmit() {
    if (!partnerInput.trim()) return alert("거래처를 입력해주세요.");
    if (!amount || Number(amount) <= 0) return alert("금액을 입력해주세요.");
    if (!title.trim()) return alert("제목을 입력해주세요.");
    if (approvers.length === 0) {
      alert("결재선을 등록해주세요.");
      return;
    }
    setShowApprovalConfirm(true);
  }

  async function doSubmit() {
    setShowApprovalConfirm(false);
    setSaving(true);
    try {
      const approval_lines_data = approvers.map((a, idx) => ({
        approver_id: a.id,
        approval_type: a.status || "결재",
        order: idx + 1,
        position: a.position || "",
      }));

      const isOther = subcategory === OTHER_CODE;
      const payload = {
        draft_date: draftDate,
        claim_date: claimDate || null,
        work_month: workMonth,
        partner_id: partnerId,
        partner_name: resolvedPartnerName,
        category,
        subcategory: isOther ? otherCategoryLabel : subcategory,
        title,
        amount: Number(amount) || 0,
        pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
        desc,
        ...(detailPanelPayloadKey ? { [detailPanelPayloadKey]: detailRows } : {}),
        draft_document: {
          document_type: "income_draft",
          category,
          subcategory: isOther ? otherCategoryLabel : subcategory,
          detail_option: isOther ? otherCategoryLabel : subcategory,
          title,
          draft_date: draftDate,
          claim_date: claimDate || null,
          work_month: workMonth,
          // ⚠️ partner(FK)는 일부러 안 보낸다 — DraftDocument.partner는 vendor_app.Vendor를
          // 가리키는데(draft_document/models.py), 이 페이지의 partnerId는 income.models.Partner
          // (사업마다 다를 수 있는 간이 거래처 모델, config.partnerApiBase 참고) PK라 테이블이
          // 다르다. partner_name(자유텍스트)만 보내면 항상 안전 — DraftDocument.partner는
          // nullable이라 비워도 문제없다.
          partner_name: resolvedPartnerName,
          amount: Number(amount) || 0,
          pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",
          desc,
          recipient: "내부결재",
          attachment_items: attachments.filter((a) => a.name.trim()).map((a) => ({ name: a.name, count: a.count })),
          seq,
          draft_no: draftNo,
          approval_lines_data,
        },
      };

      const { data } = await api.post(combinedCreateApi, payload);
      await api.post(`/draft/documents/${data.draft_document.id}/submit/`);
      const draftId = data.draft_document.id;
      setSavedDraftId(draftId);

      const fileRows = attachments.filter((a) => a.file);
      if (fileRows.length > 0) {
        setUploadingFiles(true);
        const failures = await uploadAttachmentFiles(draftId, fileRows);
        setUploadingFiles(false);
        setFailedUploads(failures);
        if (failures.length > 0) {
          alert(
            `⚠️ 기안 문서는 저장됐지만 파일 ${failures.length}개 업로드에 실패했습니다.\n` +
            `기안번호: ${data.draft_document.draft_no}\n아래에서 다시 시도해주세요.` +
            (draftNumberChangedLine(data.draft_number) ? `\n\n${draftNumberChangedLine(data.draft_number)}` : "")
          );
          return;
        }
      }

      previewChannelRef.current?.postMessage({ type: "clear" });
      try {
        new BroadcastChannel(listRefreshChannelName).postMessage({ type: "refresh" });
      } catch {}
      const changed = draftNumberChangedLine(data.draft_number);
      alert(`✅ 저장 및 상신 완료!\n기안번호: ${data.draft_document.draft_no}${changed ? `\n\n${changed}` : ""}`);
      navigate(listRoutePath);
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert(formatDraftSaveError(err));
    } finally {
      setSaving(false);
    }
  }

  // ── 렌더 ──────────────────────────────────────────────────
  if (step === "pick") {
    return (
      <div style={{ padding: 16 }}>
        <h2>{businessLabel} 수입데이터+기안 작성</h2>
        <p style={{ color: "#666", fontSize: 13 }}>수입 종류를 선택하세요. 데이터 입력과 기안 문서가 함께 만들어집니다.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, maxWidth: 900 }}>
          {categories.map((c) => (
            <button
              key={c.value}
              onClick={() => pickCategory(c.value)}
              style={{ ...PICK_BTN_STYLE, border: "1px solid #ccc", background: "#fff" }}
            >
              {c.label}
            </button>
          ))}
          <button
            onClick={() => pickCategory(OTHER_CODE)}
            style={{ ...PICK_BTN_STYLE, border: "1px dashed #999", background: "#fafafa" }}
          >
            {otherCategoryLabel}
          </button>
        </div>
      </div>
    );
  }

  const totalAttachedFiles = attachments.filter((a) => a.file).length;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <h2 style={{ margin: 0, whiteSpace: "nowrap" }}>
          {businessLabel} 수입데이터+기안 작성 — {subcategory === OTHER_CODE ? otherCategoryLabel : subcategory}
        </h2>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={backToPick} style={HEADER_BTN_STYLE}>← 종류 다시 선택</button>
          <button onClick={() => openPreviewWindow("print")} style={HEADER_BTN_STYLE}>🖨 기안 인쇄</button>
          <button onClick={() => openPreviewWindow("preview")} style={HEADER_BTN_STYLE}>👁️ PDF 미리보기</button>
          <button onClick={() => openPreviewWindow("download")} style={HEADER_BTN_STYLE}>📥 PDF 다운로드</button>
          <button
            onClick={savedDraftId ? retryFailedUploads : handleSubmit}
            disabled={saving || uploadingFiles}
            style={{
              ...SAVE_HEADER_BTN_STYLE,
              marginLeft: 12,
              background: saving || uploadingFiles ? "#999" : SAVE_HEADER_BTN_STYLE.background,
              cursor: saving || uploadingFiles ? "default" : "pointer",
            }}
          >
            {uploadingFiles ? "파일 업로드 중…" : savedDraftId ? "첨부파일 다시 시도" : saving ? "저장 중…" : "저장 및 상신"}
          </button>
        </div>
      </div>

      {failedUploads.length > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
          padding: "8px 12px", background: "#fde8e8", border: "1px solid #f5b5b5", borderRadius: 6,
          color: "#7a1f1f", fontSize: 13,
        }}>
          <span>⚠️ 기안 문서는 저장됐지만 다음 파일 업로드에 실패했습니다: {failedUploads.map((f) => f.name).join(", ")}</span>
          <button onClick={retryFailedUploads} disabled={uploadingFiles} style={{ ...HEADER_BTN_STYLE, background: "#7a1f1f", color: "#fff", border: "none" }}>
            {uploadingFiles ? "재시도 중…" : "다시 시도"}
          </button>
        </div>
      )}

      {previewBlocked && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
          padding: "8px 12px", background: "#fff3cd", border: "1px solid #ffe08a", borderRadius: 6,
          color: "#664d03", fontSize: 13,
        }}>
          <span>브라우저가 미리보기 팝업 창을 차단했습니다. 주소창의 팝업 차단 아이콘을 허용하거나, 아래 버튼으로 다시 열어주세요.</span>
          <button onClick={() => openPreviewWindow("preview")} style={{ ...HEADER_BTN_STYLE, background: "#664d03", color: "#fff", border: "none" }}>
            미리보기 창 열기
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 16 }}>
        {/* 왼쪽: 입력 폼 */}
        <div style={{ flex: "0 0 420px", display: "flex", flexDirection: "column" }}>
          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>기안번호(자동)</div>
            <div style={{ minWidth: 0 }}><input value={draftNo} readOnly style={ROW_INPUT_READONLY_STYLE} /></div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>작성일</div>
            <div style={{ minWidth: 0 }}>
              <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} style={ROW_INPUT_STYLE} />
            </div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>청구일</div>
            <div style={{ minWidth: 0 }}>
              <input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} style={ROW_INPUT_STYLE} />
            </div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>귀속월</div>
            <div style={{ minWidth: 0 }}>
              <input type="month" value={workMonth} onChange={(e) => setWorkMonth(e.target.value)} style={ROW_INPUT_STYLE} />
            </div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>제목</div>
            <div style={{ minWidth: 0 }}><input value={title} onChange={(e) => setTitle(e.target.value)} style={ROW_INPUT_STYLE} /></div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>거래처</div>
            <div style={{ minWidth: 0, position: "relative" }}>
              <input
                value={partnerInput}
                onChange={(e) => { setPartnerInput(e.target.value); setPartnerId(null); setShowPartnerList(true); setPartnerHighlight(-1); }}
                onFocus={() => partnerInput.trim() && setShowPartnerList(true)}
                onKeyDown={(e) => {
                  if (!showPartnerList || !filteredPartners.length) return;
                  if (e.key === "ArrowDown") { e.preventDefault(); setPartnerHighlight((i) => (i < filteredPartners.length - 1 ? i + 1 : 0)); }
                  else if (e.key === "ArrowUp") { e.preventDefault(); setPartnerHighlight((i) => (i > 0 ? i - 1 : filteredPartners.length - 1)); }
                  else if (e.key === "Enter" && partnerHighlight >= 0) {
                    e.preventDefault();
                    const p = filteredPartners[partnerHighlight];
                    setPartnerInput(p.name); setPartnerId(p.id); setShowPartnerList(false);
                  } else if (e.key === "Escape") setShowPartnerList(false);
                }}
                style={ROW_INPUT_STYLE}
                autoComplete="off"
              />
              {showPartnerList && filteredPartners.length > 0 && (
                <ul style={{
                  position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
                  border: "1px solid #d1d5db", borderRadius: 4, maxHeight: 240, overflowY: "auto",
                  zIndex: 20, boxShadow: "0 4px 6px rgba(0,0,0,0.15)", marginTop: 2, padding: 0, listStyle: "none",
                }}>
                  {filteredPartners.map((p, i) => (
                    <li
                      key={p.id}
                      onMouseDown={() => { setPartnerInput(p.name); setPartnerId(p.id); setShowPartnerList(false); }}
                      style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, background: i === partnerHighlight ? "#f0f9ff" : "#fff" }}
                    >
                      {p.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>금액</div>
            <div style={{ minWidth: 0 }}>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))} style={ROW_INPUT_STYLE} />
            </div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>거래방법</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", height: 36 }}>
              {PAY_METHOD_LABELS.map((label) => (
                <label key={label} style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <input type="radio" checked={payMethodLabel === label} onChange={() => setPayMethodLabel(label)} /> {label}
                </label>
              ))}
            </div>
          </div>

          <div style={ROW_GRID_STYLE}>
            <div style={ROW_LABEL_STYLE}>본문/비고</div>
            <div style={{ minWidth: 0 }}>
              <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} style={{ ...ROW_TEXTAREA_STYLE, overflow: "hidden", resize: "none" }} />
            </div>
          </div>
        </div>

        {/* 오른쪽: (종류별 상세내역) + 붙임/결재라인 */}
        <div style={{ flex: 1, minWidth: 480 }}>
          {showDetailPanel && <DetailPanel key={subcategory} onRowsChange={setDetailRows} />}
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <strong>붙임</strong>
                <span style={{
                  fontSize: 12, fontWeight: 600, color: totalAttachedFiles > 0 ? "#1e7b34" : "#999",
                  paddingLeft: 10, borderLeft: "1px solid #ddd", lineHeight: `${ATTACHMENT_ROW_HEIGHT}px`,
                }}>
                  첨부 파일 총 {totalAttachedFiles}건
                </span>
                <button type="button" onClick={addAttachment} style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_ROW_HEIGHT, color: BTN_TEXT_COLOR, marginLeft: 4 }} title="텍스트로만 추가">+</button>
                <input ref={fileInputRef} type="file" multiple onChange={handleFilesSelected} style={{ display: "none" }} />
                <button type="button" onClick={() => fileInputRef.current?.click()} style={{ ...HEADER_BTN_STYLE, ...ATTACHMENT_ROW_ITEM_STYLE, padding: "0 12px", fontSize: 13 }}>📎 파일 첨부</button>
              </div>
              {attachments.map((a) => (
                <div key={a.id} style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
                  <input
                    value={a.name}
                    onChange={(e) => setAttachmentField(a.id, "name", e.target.value)}
                    style={{ ...ATTACHMENT_ROW_ITEM_STYLE, flex: 1, minWidth: 0, position: "relative", top: 2 }}
                    placeholder="붙임 이름"
                  />
                  <span style={{
                    ...ATTACHMENT_ROW_ITEM_STYLE,
                    width: ATTACHMENT_STATUS_COL_WIDTH, flexShrink: 0, textAlign: "right",
                    fontSize: 12, fontWeight: a.file ? 600 : 400, color: a.file ? "#1e7b34" : "#999", whiteSpace: "nowrap",
                    lineHeight: `${ATTACHMENT_ROW_HEIGHT}px`,
                  }}>
                    첨부 파일 {a.file ? 1 : 0}건
                  </span>
                  <div style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_ACTION_COL_WIDTH, flexShrink: 0 }}>
                    {a.file ? (
                      <label title="클릭하여 파일 재선택" style={{
                        ...ATTACHMENT_ROW_ITEM_STYLE, display: "flex", alignItems: "center", gap: 6, width: "100%",
                        padding: "0 10px", background: "#e6f4ea", border: "1px solid #a6d8b3", borderRadius: 6,
                        color: "#1e7b34", fontSize: 12, cursor: "pointer",
                      }}>
                        <span style={{ flexShrink: 0 }}>✓</span>
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.file.name}</span>
                        <span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>({formatFileSize(a.file.size)})</span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAttachmentField(a.id, "file", null); }}
                          style={{ width: 16, height: 16, fontSize: 10, lineHeight: 1, padding: 0, color: "#1e7b34", flexShrink: 0 }}
                          title="첨부 파일만 제거"
                        >✕</button>
                        <input type="file" onChange={(e) => { const file = e.target.files?.[0]; if (file) setAttachmentField(a.id, "file", file); e.target.value = ""; }} style={{ display: "none" }} />
                      </label>
                    ) : (
                      <label style={{ ...HEADER_BTN_STYLE, ...ATTACHMENT_ROW_ITEM_STYLE, width: "100%", padding: "0 10px", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        📎 파일 첨부
                        <input type="file" onChange={(e) => { const file = e.target.files?.[0]; if (file) setAttachmentField(a.id, "file", file); e.target.value = ""; }} style={{ display: "none" }} />
                      </label>
                    )}
                  </div>
                  <input
                    type="number"
                    value={a.count}
                    onChange={(e) => setAttachmentField(a.id, "count", Number(e.target.value) || 1)}
                    style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_COUNT_COL_WIDTH, flexShrink: 0, position: "relative", top: 2 }}
                  />
                  <span style={{ width: ATTACHMENT_UNIT_COL_WIDTH, flexShrink: 0, lineHeight: `${ATTACHMENT_ROW_HEIGHT}px` }}>부</span>
                  <button type="button" onClick={() => removeAttachment(a.id)} style={{ ...ATTACHMENT_ROW_ITEM_STYLE, width: ATTACHMENT_REMOVE_COL_WIDTH, color: BTN_TEXT_COLOR, flexShrink: 0 }}>✕</button>
                </div>
              ))}
            </div>

            <div style={{ flex: "0 0 520px" }}>
              <ApprovalLine
                approvers={approvers}
                setApprovers={setApprovers}
                onOpenModal={() => setShowApprovalModal(true)}
                documentType="income_draft"
              />
            </div>
          </div>
        </div>
      </div>

      {showApprovalModal && (
        <ApprovalLineModal
          onClose={() => setShowApprovalModal(false)}
          onSelect={(selected) => { setApprovers(selected); setShowApprovalModal(false); }}
          documentType="income_draft"
          initialApprovers={approvers}
        />
      )}
      {showApprovalConfirm && (
        <ApprovalLineConfirmDialog approvers={approvers} onCancel={() => setShowApprovalConfirm(false)} onConfirm={doSubmit} />
      )}
    </div>
  );
}
